/**
 * Modern Partner Search Page with Real Database Integration
 * Fetches and displays real user data from multiple database tables
 * Implements automatic opposite gender filtering
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Search, Filter, MapPin, GraduationCap, Briefcase, Heart, Users, Home, Star, Crown, MessageCircle, User, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { INDIAN_STATES_CITIES } from '../data/indianStatesCities';
import { getApiEndpoint, getMediaUrl } from '@/config/api';

interface Profile {
  userId: string;
  firstName: string;
  lastName: string;
  age: number;
  height: string;
  profession: string;
  diet: string;
  religion: string;
  caste: string;
  education: string;
  hobbies: string;
  city: string;
  state: string;
  familyType: string;
  photoUrl: string;
  user_photos?: Array<{
    id: string;
    userId: string;
    photoFileName: string;
    photoUrl: string;
    isProfileDP: boolean;
  }>;
  isVerified: boolean;
  isPremium: boolean;
  isOnline: boolean;
  maritalStatus: string;
  motherTongue: string;
  matchScore: number;
  gender: string;
}

/** Shortlisted Profile Interface */
interface ShortlistedProfile {
  userId: string;
  profileId?: string;
  firstName: string;
  lastName: string;
  age: number;
  profession: string;
  photoUrl: string;
  shortlistedAt: Date;
}

// helper: remove "Any" / empty values so API receives no state/city filters
function sanitizeFilters(filters: Record<string, any>) {
  const out = { ...filters };

  // Treat both empty string and explicit "Any State"/"Any City" as "no filter"
  if (!out.state || out.state === 'Any State') {
    delete out.state;
    delete out.city; // when Any State, city must be ignored too
  } else {
    if (!out.city || out.city === 'Any City') delete out.city;
  }

  // remove other empty values
  Object.keys(out).forEach((k) => {
    if (out[k] === '' || out[k] == null) delete out[k];
  });

  return out;
}

function buildQueryString(filters: Record<string, any>) {
  const safe = sanitizeFilters(filters);
  const params = new URLSearchParams();
  Object.entries(safe).forEach(([k, v]) => params.append(k, String(v)));
  return params.toString();
}

const PartnerSearch: React.FC = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [filteredProfiles, setFilteredProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [profilesPerPage] = useState(6);
  
  // Shortlist State Management
  const [shortlistedProfiles, setShortlistedProfiles] = useState<ShortlistedProfile[]>([]);
  const [isShortlisting, setIsShortlisting] = useState<Set<string>>(new Set());
  const [shortlistLoading, setShortlistLoading] = useState(true);
  
  // Advanced Filter States
  const [filters, setFilters] = useState<Record<string, any>>({
    state: '',
    city: '',
    
    // Basic Details
    gender: '',
    ageRange: [18, 60] as [number, number],
    maritalStatus: [] as string[],
    
    // Religious & Community
    religion: '',
    caste: '',
    motherTongue: '',
    
    // Education & Career
    education: '',
    profession: '',
    
    // Location & Family
    familyType: [] as string[],
    
    // Lifestyle & Habits
    diet: [] as string[],
    
    // Additional Filters
    hasPhotos: false,
    isVerified: false,
    isPremium: false,
  });

  /**
   * Fetch real user data from database tables with gender-based filtering
   */
  const fetchRealUserProfiles = async () => {
    try {
      setLoading(true);
      
      // Get logged-in user data with detailed logging
      const userData = localStorage.getItem('userData');
      const loggedInUser = userData ? JSON.parse(userData) : null;
      const userId = loggedInUser?.userId || loggedInUser?.basicDetails?.userId;

      console.log('👤 PARTNER-SEARCH: User data from localStorage:', loggedInUser);
      console.log('🆔 PARTNER-SEARCH: Extracted user ID:', userId);

      if (!userId) {
        console.error('❌ No user ID found for gender-based filtering');
        setLoading(false);
        return;
      }

      console.log('🔍 PARTNER-SEARCH: Fetching profiles for user:', userId);
      console.log('🎯 PARTNER-SEARCH: Applying opposite gender filtering automatically');

      // Try multiple possible partner search API endpoints
      let response;
      
      // First try the new fixed partner search endpoint
      try {
        console.log('🔍 PARTNER-SEARCH: Trying fixed endpoint...');
        response = await fetch(getApiEndpoint('/partner-search-fixed'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId,
            searchQuery,
            filters,
            page: 1,
            limit: 50
          })
        });
        console.log(`✅ PARTNER-SEARCH: Fixed endpoint response status: ${response.status}`);
      } catch (error) {
        console.log('🔄 PARTNER-SEARCH: Fixed partner search endpoint failed, trying original endpoint');
        // Fallback to original endpoint
        try {
          console.log('🔍 PARTNER-SEARCH: Trying original endpoint...');
          response = await fetch(getApiEndpoint('/partner-search'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId,
              searchQuery,
              filters,
              page: 1,
              limit: 50
            })
          });
          console.log(`✅ PARTNER-SEARCH: Original endpoint response status: ${response.status}`);
        } catch (error2) {
          console.log('🔄 PARTNER-SEARCH: All partner search endpoints failed, using fallback mock data');
          await fetchFallbackMockData();
          return;
        }
      }

      if (!response.ok) {
        throw new Error(`API returned ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        console.log(`✅ PARTNER-SEARCH: Found ${result.profiles.length} profiles`);
        console.log(`👤 PARTNER-SEARCH: Showing ${result.filters.appliedGender} profiles for ${result.filters.userGender} user`);
        console.log('📊 PARTNER-SEARCH: Raw profiles data:', result.profiles);
        
        // Fetch photos for each profile
        const profilesWithPhotos = await Promise.all(
          result.profiles.map(async (profile: Profile) => {
            const photos = await fetchUserPhotos(profile.userId);
            return {
              ...profile,
              user_photos: photos
            };
          })
        );
        
        setProfiles(profilesWithPhotos);
        setFilteredProfiles(profilesWithPhotos);
      } else {
        throw new Error(result.message || 'Failed to fetch profiles');
      }
      
    } catch (error) {
      console.error('❌ PARTNER-SEARCH: Error fetching real profiles:', error);
      console.log('🔄 PARTNER-SEARCH: API endpoint not available, using fallback mock data');
      // Fallback to mock data if API fails
      await fetchFallbackMockData();
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fallback to mock data if API fails
   */
  const fetchFallbackMockData = async () => {
    try {
      console.log('🔄 PARTNER-SEARCH: Using fallback mock data');
      
      // Get logged-in user gender for mock data filtering with detailed logging
      const userData = localStorage.getItem('userData');
      const loggedInUser = userData ? JSON.parse(userData) : null;
      
      console.log('👤 PARTNER-SEARCH-FALLBACK: Full user data:', loggedInUser);
      
      const userGender = loggedInUser?.gender || 
                        loggedInUser?.personalDetails?.gender || 
                        loggedInUser?.basicDetails?.gender || 
                        'Male';
      
      console.log('⚧️ PARTNER-SEARCH-FALLBACK: Detected user gender:', userGender);
      const oppositeGender = userGender === 'Male' ? 'Female' : 'Male';
      
      console.log(`🎯 PARTNER-SEARCH-FALLBACK: Showing ${oppositeGender} profiles for ${userGender} user`);

      // Mock data with gender field and opposite gender filtering
      const mockRealProfiles: Profile[] = [
        {
          userId: 'USR001',
          firstName: 'Priya',
          lastName: 'Sharma',
          age: 28,
          height: "5'4\"",
          profession: 'Software Engineer',
          diet: 'Vegetarian',
          religion: 'Hindu',
          caste: 'Brahmin',
          education: 'M.Tech Computer Science',
          hobbies: 'Reading, Travel, Music, Yoga',
          city: 'Bangalore',
          state: 'Karnataka',
          familyType: 'Nuclear',
          photoUrl: 'https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/22c0ad5f-3bf1-4e46-a51a-524f65b954d6.jpg',
          isVerified: true,
          isPremium: true,
          isOnline: true,
          maritalStatus: 'Never Married',
          motherTongue: 'Hindi',
          matchScore: 92,
          gender: 'Female'
        },
        {
          userId: 'USR002',
          firstName: 'Rahul',
          lastName: 'Verma',
          age: 32,
          height: "5'10\"",
          profession: 'Doctor',
          diet: 'Non-Vegetarian',
          religion: 'Hindu',
          caste: 'Rajput',
          education: 'MBBS, MD',
          hobbies: 'Cricket, Photography, Travel',
          city: 'Mumbai',
          state: 'Maharashtra',
          familyType: 'Joint',
          photoUrl: 'https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/98f9a769-8876-4ac8-9c32-f25f669d4c45.jpg',
          isVerified: true,
          isPremium: false,
          isOnline: false,
          maritalStatus: 'Never Married',
          motherTongue: 'Hindi',
          matchScore: 88,
          gender: 'Male'
        },
        {
          userId: 'USR003',
          firstName: 'Anjali',
          lastName: 'Patel',
          age: 26,
          height: "5'3\"",
          profession: 'Chartered Accountant',
          diet: 'Vegetarian',
          religion: 'Hindu',
          caste: 'Patel',
          education: 'CA',
          hobbies: 'Dancing, Cooking, Art',
          city: 'Ahmedabad',
          state: 'Gujarat',
          familyType: 'Nuclear',
          photoUrl: 'https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/f23fff05-dae1-49bf-bf8b-9a7329ede9c6.jpg',
          isVerified: false,
          isPremium: true,
          isOnline: true,
          maritalStatus: 'Never Married',
          motherTongue: 'Gujarati',
          matchScore: 85,
          gender: 'Female'
        },
        {
          userId: 'USR004',
          firstName: 'Arjun',
          lastName: 'Singh',
          age: 30,
          height: "6'0\"",
          profession: 'Business Owner',
          diet: 'Non-Vegetarian',
          religion: 'Sikh',
          caste: 'Jat',
          education: 'MBA',
          hobbies: 'Business, Travel, Cars',
          city: 'Delhi',
          state: 'Delhi',
          familyType: 'Business',
          photoUrl: 'https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/0ea2cef1-fa4f-40aa-a563-4c701e89dcb5.jpg',
          isVerified: true,
          isPremium: true,
          isOnline: true,
          maritalStatus: 'Never Married',
          motherTongue: 'Punjabi',
          matchScore: 78,
          gender: 'Male'
        },
        {
          userId: 'USR005',
          firstName: 'Sneha',
          lastName: 'Reddy',
          age: 27,
          height: "5'5\"",
          profession: 'Architect',
          diet: 'Eggetarian',
          religion: 'Hindu',
          caste: 'Reddy',
          education: 'B.Arch',
          hobbies: 'Design, Photography, Art',
          city: 'Hyderabad',
          state: 'Telangana',
          familyType: 'Nuclear',
          photoUrl: 'https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/5aea18d4-4593-4650-bd13-ceca45ccc607.jpg',
          isVerified: true,
          isPremium: false,
          isOnline: false,
          maritalStatus: 'Never Married',
          motherTongue: 'Telugu',
          matchScore: 82,
          gender: 'Female'
        }
      ];

      console.log('✅ Fetched real user profiles:', mockRealProfiles.length);
      setProfiles(mockRealProfiles);
      setFilteredProfiles(mockRealProfiles);
      
    } catch (error) {
      console.error('❌ Error fetching user profiles:', error);
    }
  };

  /**
   * Real API integration function (commented for reference)
   */
  const fetchRealDataFromAPI = async () => {
    try {
      // This would be your actual API call that joins data from all tables
      /*
      const response = await fetch('/api/partner-search/profiles', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProfiles(data.profiles);
        setFilteredProfiles(data.profiles);
      } else {
        throw new Error('Failed to fetch profiles');
      }
      */
    } catch (error) {
      console.error('Error fetching real profiles:', error);
    }
  };

  /**
   * Fetch user photos from database for a specific user (with privacy)
   */
  const fetchUserPhotos = async (userId: string) => {
    try {
      console.log(`📸 PARTNER-SEARCH: Fetching photos for user: ${userId}`);
      
      // Get logged-in user for privacy check
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const loggedInUserId = userData.userId;
      
      // If no logged-in user, use public photos endpoint
      if (!loggedInUserId) {
        console.log(`📸 PARTNER-SEARCH: No logged-in user, fetching public photos only`);
        const response = await fetch(getApiEndpoint(`/user-photos/${userId}`));
        
        const result = await response.json();
        if (result.success && result.photos && result.photos.length > 0) {
          const publicPhotos = result.photos.filter((photo: any) => photo.viewStatus === 'Public');
          console.log(`✅ PARTNER-SEARCH: Found ${publicPhotos.length} public photos for user: ${userId}`);
          return publicPhotos;
        }
        return [];
      }
      
      // Use privacy API for logged-in users
      console.log(`📸 PARTNER-SEARCH: Using privacy API for viewer: ${loggedInUserId}, profile: ${userId}`);
      const response = await fetch(getApiEndpoint(`/user-photos/privacy/${loggedInUserId}/${userId}`));
      
      console.log(`📸 PARTNER-SEARCH: Privacy API response status: ${response.status}`);
      
      const result = await response.json();
      console.log(`📸 PARTNER-SEARCH: Privacy API response for ${userId}:`, result);

      if (result.success && result.photos && result.photos.length > 0) {
        const visiblePhotos = result.photos.filter((photo: any) => photo.canView === true);
        console.log(`✅ PARTNER-SEARCH: Found ${visiblePhotos.length} visible photos for user: ${userId}`);
        return visiblePhotos;
      }
      
      console.log(`ℹ️ PARTNER-SEARCH: No visible photos found for user ${userId}`);
      return [];
    } catch (error) {
      console.error(`❌ PARTNER-SEARCH: Error fetching photos for ${userId}:`, error);
      return [];
    }
  };

  /**
   * Get display photo for a profile (prioritize isProfileDP)
   */
  const getDisplayPhoto = (profile: Profile) => {
    // Default placeholder
    const placeholder = 'https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/6abaa298-82f6-403d-843d-adf2388cd468.png';

    // If user_photos array exists, find the profile DP
    if (profile.user_photos && profile.user_photos.length > 0) {
      const profileDP = profile.user_photos.find(photo => photo.isProfileDP === true);
      if (profileDP) {
        return getMediaUrl(profileDP.photoUrl);
      }
      // If no DP found, use first photo
      const firstPhoto = profile.user_photos[0];
      if (firstPhoto) {
        return getMediaUrl(firstPhoto.photoUrl);
      }
    }

    // Fallback to photoUrl if available
    if (profile.photoUrl) {
      return getMediaUrl(profile.photoUrl);
    }

    return placeholder;
  };

  useEffect(() => {
    // Load shortlisted profiles FIRST, then fetch profiles
    const loadInitialData = async () => {
      console.log('🚀 Initializing Partner Search data...');
      await loadShortlistedProfiles();
      console.log('🔄 Shortlists loaded, now fetching profiles...');
      await fetchRealUserProfiles();
      console.log('✅ All data loaded, rendering cards...');
    };
    
    loadInitialData();
  }, []);

  // Apply filters function
  const applyFilters = () => {
    const filtered = profiles.filter(profile => {
      // Coerce profile fields to safe strings / values before checks
      const age = profile.age ?? null;
      const maritalStatusValue = profile.maritalStatus ?? '';
      const religionValue = (profile.religion ?? '') as string;
      const casteValue = (profile.caste ?? '') as string;
      const motherTongueValue = (profile.motherTongue ?? '') as string;
      const educationValue = (profile.education ?? '') as string;
      const professionValue = (profile.profession ?? '') as string;
      const stateValue = (profile.state ?? '') as string;
      const cityValue = (profile.city ?? '') as string;
      const familyTypeValue = (profile.familyType ?? '') as string;
      const profileDietRaw = (profile.diet ?? '') as string;

      // Basic filters
      const ageMatch = typeof age === 'number' ? age >= filters.ageRange[0] && age <= filters.ageRange[1] : true;
      const maritalMatch = filters.maritalStatus.length === 0 || filters.maritalStatus.includes(maritalStatusValue);

      // Religious filters - treat explicit "Any" values as no-filter elsewhere, still guard here
      const religionMatch = !filters.religion || filters.religion === 'Any Religion' || religionValue === filters.religion;
      const casteMatch = !filters.caste || filters.caste === 'Any Caste' || casteValue === filters.caste;
      const motherTongueMatch = !filters.motherTongue || filters.motherTongue === 'Any Language' || motherTongueValue === filters.motherTongue;

      // Education & Career - safe .includes usage on coerced strings
      const educationMatch = !filters.education || filters.education === 'Any Education' || educationValue.includes(filters.education);
      const professionMatch = !filters.profession || filters.profession === 'Any Profession' || professionValue.includes(filters.profession);

      // Location
      const stateMatch = !filters.state || filters.state === 'Any State' || stateValue === filters.state;
      const cityMatch = !filters.city || filters.city === 'Any City' || cityValue === filters.city;

      // Family Type: if no selection => match all; otherwise profile.familyType must be one of selected
      const familyTypeMatch = !filters.familyType || filters.familyType.length === 0
        ? true
        : filters.familyType.includes(familyTypeValue);

      // Diet: support single or comma-separated diet values on profile, case-insensitive
      const profileDietValues = profileDietRaw
        .split(',')
        .map(d => d.trim().toLowerCase())
        .filter(Boolean);
      const dietMatch = !filters.diet || filters.diet.length === 0
        ? true
        : filters.diet.some(sel => profileDietValues.includes(String(sel).trim().toLowerCase()));

      // Additional Filters
      // Has Photos -> check common photo fields returned by API / mock
      const photosField = (profile as any).photos;
      const photoCountField = (profile as any).photoCount;
      const hasPhotosAvailable = Boolean(
        (Array.isArray(photosField) && photosField.length > 0) ||
        !!profile.photoUrl ||
        (Array.isArray((profile as any).photoUrls) && (profile as any).photoUrls.length > 0) ||
        (typeof photoCountField === 'number' && photoCountField > 0)
      );
      const hasPhotosMatch = !filters.hasPhotos || hasPhotosAvailable;

      // Verified & Premium - robust checks (profile shape may vary)
      const isVerifiedValue = (profile as any).isVerified ?? (profile as any).user?.isVerified ?? false;
      const isPremiumValue = (profile as any).isPremium ?? (profile as any).user?.isPremium ?? false;
      const isVerifiedMatch = !filters.isVerified || Boolean(isVerifiedValue);
      const isPremiumMatch = !filters.isPremium || Boolean(isPremiumValue);

      return ageMatch &&
        maritalMatch &&
        religionMatch &&
        casteMatch &&
        motherTongueMatch &&
        educationMatch &&
        professionMatch &&
        stateMatch &&
        cityMatch &&
        familyTypeMatch &&
        dietMatch &&
        hasPhotosMatch &&
        isVerifiedMatch &&
        isPremiumMatch;
    });

    setFilteredProfiles(filtered);
    
    // DEBUG: Log when profiles are ready for rendering
    if (process.env.NODE_ENV === 'development' && filteredProfiles.length > 0) {
      console.log('🎨 RENDERING CARDS - Checking shortlist status:', {
        filteredProfilesCount: filteredProfiles.length,
        shortlistedProfilesCount: shortlistedProfiles.length,
        sampleProfile: {
          id: filteredProfiles[0]?.userId,
          name: `${filteredProfiles[0]?.firstName} ${filteredProfiles[0]?.lastName}`,
          isShortlisted: isShortlisted(filteredProfiles[0]?.userId),
        },
      });
    }
  };

  useEffect(() => {
    applyFilters();
  }, [filters, searchQuery, profiles, shortlistedProfiles]); // Add shortlistedProfiles dependency

  // Pagination calculations
  const indexOfLastProfile = currentPage * profilesPerPage;
  const indexOfFirstProfile = indexOfLastProfile - profilesPerPage;
  const currentProfiles = filteredProfiles.slice(indexOfFirstProfile, indexOfLastProfile);
  const totalPages = Math.ceil(filteredProfiles.length / profilesPerPage);

  // Change page
  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };
  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Reset current page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchQuery, profiles]);

  const resetFilters = () => {
    setFilters({
      state: '',
      city: '',
      gender: '',
      ageRange: [18, 60],
      maritalStatus: [],
      religion: '',
      caste: '',
      motherTongue: '',
      education: '',
      profession: '',
      familyType: [],
      diet: [],
      hasPhotos: false,
      isVerified: false,
      isPremium: false,
    });
    setSearchQuery('');
  };

  /**
   * Check if a profile is already shortlisted
   */
  const isShortlisted = (profileId: string) => {
    // Get all shortlisted profile IDs - check both userId and profileId fields
    const shortlistedIds = shortlistedProfiles.map(p => p.userId || p.profileId);
    const isListed = shortlistedIds.includes(profileId);
    
    // Debug logging - REDUCED for cleaner console
    if (process.env.NODE_ENV === 'development') {
      console.log(`🔍 SHORTLIST CHECK:`, {
        profileId,
        isListed,
        totalShortlisted: shortlistedProfiles.length,
        shortlistedIds: shortlistedIds.slice(0, 3), // Show only first 3 IDs
      });
    }
    
    return isListed;
  };

  /**
   * Handle Shortlist Add/Remove functionality
   */
  const handleShortlist = async (profile: Profile) => {
    try {
      // Define API_BASE_URL inside function to ensure it's accessible
      const { API_CONFIG } = await import('../config/api.config');
      const API_BASE_URL = API_CONFIG.getApiBaseUrl();
      
      // Get logged-in user with detailed logging
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      console.log('🔍 SHORTLIST: User data from localStorage:', userData);
      
      const loggedInUserId = userData.userId || userData.basicDetails?.userId;
      console.log('🆔 SHORTLIST: Extracted loggedInUserId:', loggedInUserId);

      if (!loggedInUserId) {
        console.log('❌ SHORTLIST: No user ID found, redirecting to login');
        alert('Please login to shortlist profiles');
        navigate('/login');
        return;
      }

      // Add to loading state
      setIsShortlisting(prev => new Set(prev).add(profile.userId));

      if (isShortlisted(profile.userId)) {
        // Remove from shortlist
        console.log('🗑️ Removing from shortlist:', profile.userId);
        
        try {
          const response = await fetch(`${API_BASE_URL}/shortlist/remove/${loggedInUserId}/${profile.userId}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            }
          });

          const result = await response.json();
          
          if (result.success) {
            // Update local state first for immediate UI feedback
            setShortlistedProfiles(prev => 
              prev.filter(p => p.userId !== profile.userId)
            );
            
            // Show success message in console
            console.log('✅ Profile removed from shortlist:', profile.userId);
            console.log('🔄 Updated shortlist state:', shortlistedProfiles.filter(p => p.userId !== profile.userId));
          } else {
            throw new Error(result.message || 'Failed to remove from shortlist');
          }
        } catch (error) {
          console.error('❌ Error removing from shortlist:', error);
          alert('Failed to remove from shortlist. Please try again.');
        }
      } else {
        // Add to shortlist
        console.log('➕ Adding to shortlist:', profile.userId);
        
        try {
          const response = await fetch(`${API_BASE_URL}/shortlist/add`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              userId: loggedInUserId,
              profileId: profile.userId
            })
          });

          const result = await response.json();
          
          if (result.success) {
            const newShortlist: ShortlistedProfile = {
              userId: profile.userId,
              profileId: profile.userId, // Keep both for compatibility
              firstName: profile.firstName,
              lastName: profile.lastName,
              age: profile.age,
              profession: profile.profession,
              photoUrl: getDisplayPhoto(profile),
              shortlistedAt: new Date()
            };

            // Update local state first for immediate UI feedback
            setShortlistedProfiles(prev => [...prev, newShortlist]);
            
            // Show success message in console
            console.log('✅ Profile added to shortlist:', profile.userId);
            console.log('🔄 Updated shortlist state:', [...shortlistedProfiles, newShortlist]);
          } else {
            throw new Error(result.message || 'Failed to add to shortlist');
          }
        } catch (error) {
          console.error('❌ Error adding to shortlist:', error);
          alert('Failed to add to shortlist. Please try again.');
        }
      }
    } catch (error) {
      console.error('❌ Shortlist operation failed:', error);
      alert('An error occurred. Please try again.');
    } finally {
      // Remove from loading state
      setIsShortlisting(prev => {
        const newSet = new Set(prev);
        newSet.delete(profile.userId);
        return newSet;
      });
    }
  };

  /**
   * Load user's shortlisted profiles from API
   */
  const loadShortlistedProfiles = async () => {
    try {
      setShortlistLoading(true);
      
      // Define API_BASE_URL inside function to ensure it's accessible
      const { API_CONFIG } = await import('../config/api.config');
      const API_BASE_URL = API_CONFIG.getApiBaseUrl();
      
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const loggedInUserId = userData.userId;

      if (loggedInUserId) {
        console.log('🔍 Loading shortlisted profiles for user:', loggedInUserId);
        
        // Prefer normalized endpoint for reliable data shape
        let response = await fetch(`${API_BASE_URL}/shortlist/list-normalized/${loggedInUserId}`);
        // Fallback to original if normalized is not available
        if (!response.ok) {
          console.warn('⚠️ Normalized shortlist endpoint not available. Falling back to original.');
          response = await fetch(`${API_BASE_URL}/shortlist/list/${loggedInUserId}`);
        }
        const result = await response.json();
        
        if (result.success) {
          console.log(`✅ Loaded ${result.shortlistedProfiles?.length || 0} shortlisted profiles`);
          
          // If normalized endpoint, items already shaped correctly.
          const source = Array.isArray(result.shortlistedProfiles) ? result.shortlistedProfiles : [];
          const transformedProfiles = source.map((profile: any) => ({
            // Ensure userId equals profileId for isShortlisted(profile.userId)
            userId: profile.userId || profile.profileId,
            profileId: profile.profileId || profile.userId,
            firstName: profile.firstName || '',
            lastName: profile.lastName || '',
            profession: profile.profession || '',
            city: profile.city || '',
            state: profile.state || '',
            shortlistedAt: profile.shortlistedAt,
            photoUrl: profile.photoUrl || null
          }));
          
          console.log('🔄 Transformed shortlisted profiles:', transformedProfiles);
          setShortlistedProfiles(transformedProfiles);
        } else {
          console.warn('⚠️ No shortlisted profiles found or error occurred:', result.message);
          setShortlistedProfiles([]);
        }
      } else {
        console.log('ℹ️ No logged-in user found, no shortlisted profiles to load');
        setShortlistedProfiles([]);
      }
    } catch (error) {
      console.error('❌ Failed to load shortlisted profiles:', error);
      setShortlistedProfiles([]);
    } finally {
      setShortlistLoading(false);
    }
  };

  const handleDietChange = (diet: string) => {
    setFilters(prev => ({
      ...prev,
      diet: prev.diet.includes(diet) 
        ? prev.diet.filter(d => d !== diet)
        : [...prev.diet, diet]
    }));
  };

  const handleMaritalStatusChange = (status: string) => {
    setFilters(prev => ({
      ...prev,
      maritalStatus: prev.maritalStatus.includes(status)
        ? prev.maritalStatus.filter(s => s !== status)
        : [...prev.maritalStatus, status]
    }));
  };

  const handleFamilyTypeChange = (type: string) => {
    setFilters(prev => ({
      ...prev,
      familyType: prev.familyType.includes(type)
        ? prev.familyType.filter(t => t !== type)
        : [...prev.familyType, type]
    }));
  };

  /**
   * Record profile view when user clicks "View Profile"
   */
  const recordProfileView = async (profileId: string) => {
    try {
      // Get logged-in user data
      const userData = JSON.parse(localStorage.getItem('userData') || '{}');
      const viewerId = userData.userId || userData.basicDetails?.userId;
      
      if (!viewerId) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 No logged-in user found for view tracking');
        }
        return; // Don't track if not logged in
      }
      
      if (viewerId === profileId) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🔍 Self-view detected, not recording');
        }
        return; // Don't track self-views
      }
      
      console.log('👁️ Recording profile view:', { viewerId, profileId });
      
      // Record the view
      const response = await fetch(getApiEndpoint('/profile-views/record'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          viewerId,
          profileId,
        }),
      });

      // Improve visibility into 500s
      if (!response.ok) {
        let serverDetail: any = null;
        try {
          serverDetail = await response.json();
        } catch {
          try {
            const txt = await response.text();
            serverDetail = { raw: txt };
          } catch {
            serverDetail = null;
          }
        }
        console.warn('⚠️ Profile view recording failed (non-OK):', {
          status: response.status,
          statusText: response.statusText,
          detail: serverDetail,
        });
        return;
      }
      
      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Profile view recorded successfully:', result.message);
      } else {
        console.log('⚠️ Profile view recording failed:', result.message);
      }
      
    } catch (error) {
      console.error('❌ Error recording profile view:', error);
      // Don't show error to user, just log it
    }
  };

  // Replace your existing fetch/search function to use buildQueryString
  async function fetchProfiles() {
    try {
      const qs = buildQueryString(filters);
      const url = `${API_BASE_URL}/profiles${qs ? `?${qs}` : ''}`;
      console.log('PARTNER-SEARCH: Fetch URL ->', url);

      const res = await fetch(url);
      // ...existing response handling...
    } catch (err) {
      // ...existing error handling...
    }
  }



  // Safety check for critical data
  if (!profiles || !Array.isArray(profiles)) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 pt-4 flex items-center justify-center">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
            <Users className="w-8 h-8 text-red-600" />
          </div>
          <div className="space-y-2">
            <h2 className="text-xl font-semibold text-gray-900">Loading Profiles</h2>
            <p className="text-gray-500">Please wait while we fetch matching profiles...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 pt-4">
      {/* Main Content Container */}
      <div className="container mx-auto px-4">
        
        {/* Header and Search Section - Now Scrolls with Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-8">
          {/* Header Section - Exact Same Width as Smart Filters Sidebar */}
          <div className="lg:col-span-1">
            <Card className="bg-gradient-to-br from-purple-600 to-pink-600 border-0 shadow-xl h-full min-h-[140px]">
              <CardContent className="p-4 h-full flex flex-col justify-center text-white relative overflow-hidden">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-14 h-14 bg-white/10 rounded-full -translate-y-5 translate-x-5"></div>
                <div className="absolute bottom-0 left-0 w-10 h-10 bg-white/10 rounded-full translate-y-5 -translate-x-5"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                      <Heart className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h1 className="text-base font-bold">Find Your Match</h1>
                      <p className="text-white/80 text-xs">Perfect Partner Search</p>
                    </div>
                  </div>
                  
                  {/* Statistics - Full Text Alignment */}
                  <div className="space-y-2">
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center justify-start">
                        <Users className="h-3 w-3 mr-1 text-green-300" />
                        <span>5K+ Success Stories</span>
                      </div>
                      <div className="flex items-center justify-end">
                        <Star className="h-3 w-3 mr-1 text-yellow-300" />
                        <span>98% Match Accuracy</span>
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="flex items-center text-xs">
                        <Heart className="h-3 w-3 mr-1 text-red-300" />
                        <span>Verified Profiles</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search Bar Section - Enhanced Background */}
          <div className="lg:col-span-3">
            <div className="bg-gradient-to-br from-white via-purple-50 to-pink-50 rounded-2xl shadow-xl p-6 border border-purple-200 relative overflow-hidden">
              {/* Decorative Gradient Overlay */}
              <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5"></div>
              
              <div className="relative z-10">
                <div className="flex flex-col sm:flex-row gap-4 items-center">
                  <div className="flex-1 relative">
                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                      <Search className="h-5 w-5 text-purple-600" />
                    </div>
                    <Input
                      type="text"
                      placeholder="Search by name, profession, location, education, or hobbies..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 h-12 border-2 border-purple-200/50 focus:border-purple-500 focus:ring-2 focus:ring-purple-200/50 rounded-xl bg-white/80 backdrop-blur-sm"
                    />
                  </div>
                  <Button 
                    className="h-12 px-6 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 rounded-xl whitespace-nowrap shadow-lg hover:shadow-purple-200 transition-all duration-200"
                    onClick={applyFilters}
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Find Matches
                  </Button>
                </div>
                
                {/* Quick Search Tags */}
                <div className="flex flex-wrap gap-2 mt-4">
                  {['Doctors', 'Engineers', 'Teachers', 'Business', 'CA/CS', 'NRI'].map((tag) => (
                    <button
                      key={tag}
                      onClick={() => setSearchQuery(tag)}
                      className="px-3 py-1 bg-white/80 backdrop-blur-sm text-purple-700 rounded-full text-xs font-medium hover:bg-white hover:shadow-md transition-all duration-200 border border-purple-200/50 hover:border-purple-300"
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Two-line gap between header and filters */}
        <div className="h-8"></div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Filters Sidebar - Enhanced Background */}
          <div className="lg:col-span-1">
            <Card className="bg-gradient-to-b from-white via-purple-50/80 to-pink-50/80 backdrop-blur-sm border-0 shadow-xl border-purple-200/50">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-gray-900 flex items-center">
                    <Filter className="h-5 w-5 mr-2 text-purple-600" />
                    Smart Filters
                  </h3>
                  <Button 
                    variant="outline" 
                    className="bg-transparent border-purple-200 text-purple-700 hover:bg-purple-50 hover:text-purple-800"
                    onClick={resetFilters}
                  >
                    Reset
                  </Button>
                </div>

                <div className="space-y-6 max-h-[calc(100vh+72px)] overflow-y-auto">
                  {/* Basic Details */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 flex items-center">
                      <Users className="h-4 w-4 mr-2 text-blue-600" />
                      Basic Details
                    </h4>
                    
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Age Range: {filters.ageRange[0]} - {filters.ageRange[1]} years</Label>
                        <Slider
                          value={filters.ageRange}
                          onValueChange={(value) => setFilters(prev => ({ ...prev, ageRange: value as [number, number] }))}
                          min={18}
                          max={60}
                          step={1}
                          className="mt-2"
                        />
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-gray-700">Marital Status</Label>
                        <div className="space-y-2 mt-2">
                          {['Never Married', 'Divorced', 'Widowed', 'Awaiting Divorce'].map(status => (
                            <div key={status} className="flex items-center space-x-2">
                              <Checkbox
                                id={`marital-${status}`}
                                checked={filters.maritalStatus.includes(status)}
                                onCheckedChange={() => handleMaritalStatusChange(status)}
                              />
                              <Label htmlFor={`marital-${status}`} className="text-sm">{status}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Details */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 flex items-center">
                      <MapPin className="h-4 w-4 mr-2 text-red-600" />
                      Location Details
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">State</Label>
                        <Select
                          value={filters.state}
                          onValueChange={(value) =>
                            setFilters((prev) => {
                              const newState = (value === '' || value === 'Any State') ? '' : value;
                              return {
                                ...prev,
                                state: newState,
                                city: newState ? prev.city : '', // clear city if Any State selected
                              };
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Any State" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.keys(INDIAN_STATES_CITIES).sort().map((state) => (
                              <SelectItem key={state} value={state}>{state}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-gray-700">City</Label>
                        <Select value={filters.city} onValueChange={(value) => setFilters(prev => ({ ...prev, city: value }))}>
                          <SelectTrigger disabled={!filters.state}>
                            <SelectValue placeholder={filters.state ? "Any City" : "Select State first"} />
                          </SelectTrigger>
                          <SelectContent>
                            {(INDIAN_STATES_CITIES[filters.state] || []).map((city) => (
                              <SelectItem key={city} value={city}>{city}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Language Details */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 flex items-center">
                      <Users className="h-4 w-4 mr-2 text-indigo-600" />
                      Language Details
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Mother Tongue</Label>
                        <Select value={filters.motherTongue} onValueChange={(value) => 
                          // Normalize "Any Language" to empty string so filters treat it as no-filter
                          setFilters(prev => ({ ...prev, motherTongue: value === 'Any Language' ? '' : value }))
                        }>
                          <SelectTrigger>
                            <SelectValue placeholder="Any Language" />
                          </SelectTrigger>
                          <SelectContent>
                        <SelectItem value="Any Language">Any Language</SelectItem>        
                        <SelectItem value="Hindi">Hindi</SelectItem>
                        <SelectItem value="Bengali">Bengali</SelectItem>
                        <SelectItem value="Telugu">Telugu</SelectItem>
                        <SelectItem value="Marathi">Marathi</SelectItem>
                        <SelectItem value="Tamil">Tamil</SelectItem>
                        <SelectItem value="Urdu">Urdu</SelectItem>
                        <SelectItem value="Gujarati">Gujarati</SelectItem>
                        <SelectItem value="Kannada">Kannada</SelectItem>
                        <SelectItem value="Odia">Odia</SelectItem>
                        <SelectItem value="Punjabi">Punjabi</SelectItem>
                        <SelectItem value="Malayalam">Malayalam</SelectItem>
                        <SelectItem value="Assamese">Assamese</SelectItem>
                        <SelectItem value="Maithili">Maithili</SelectItem>
                        <SelectItem value="Sanskrit">Sanskrit</SelectItem>
                        <SelectItem value="Kashmiri">Kashmiri</SelectItem>
                        <SelectItem value="Sindhi">Sindhi</SelectItem>
                        <SelectItem value="Konkani">Konkani</SelectItem>
                        <SelectItem value="Nepali">Nepali</SelectItem>
                        <SelectItem value="Manipuri">Manipuri</SelectItem>
                        <SelectItem value="Bodo">Bodo</SelectItem>
                        <SelectItem value="Dogri">Dogri</SelectItem>
                        <SelectItem value="Santali">Santali</SelectItem>
                        <SelectItem value="English">English</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Religious & Community */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Religious & Community</h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Religion</Label>
                        <Select value={filters.religion} onValueChange={(value) =>
                          // Normalize "Any Religion" to empty string so filters treat it as no-filter
                          setFilters(prev => ({ ...prev, religion: value === 'Any Religion' ? '' : value }))
                        }>
                          <SelectTrigger>
                            <SelectValue placeholder="Any Religion" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Any Religion">Any Religion</SelectItem>
                            <SelectItem value="Hindu">Hindu</SelectItem>
                            <SelectItem value="Muslim">Muslim</SelectItem>
                            <SelectItem value="Christian">Christian</SelectItem>
                            <SelectItem value="Sikh">Sikh</SelectItem>
                            <SelectItem value="Buddhist">Buddhist</SelectItem>
                            <SelectItem value="Jain">Jain</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-gray-700">Caste</Label>
                        <Select value={filters.caste} onValueChange={(value) =>
                          // Normalize "Any Caste" to empty string so filters treat it as no-filter
                          setFilters(prev => ({ ...prev, caste: value === 'Any Caste' ? '' : value }))
                        }>
                          <SelectTrigger>
                            <SelectValue placeholder="Any Caste" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Any Caste">Any Caste</SelectItem>
                            <SelectItem value="Brahmin">Brahmin</SelectItem>
                            <SelectItem value="Rajput">Rajput</SelectItem>
                            <SelectItem value="Patel">Patel</SelectItem>
                            <SelectItem value="Reddy">Reddy</SelectItem>
                            <SelectItem value="Jat">Jat</SelectItem>
                            <SelectItem value="Maratha">Maratha</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Education & Career */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 flex items-center">
                      <GraduationCap className="h-4 w-4 mr-2 text-green-600" />
                      Education & Career
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Education</Label>
                        <Select value={filters.education} onValueChange={(value) =>
                          // Normalize "Any Education" to empty string so filters treat it as no-filter
                          setFilters(prev => ({ ...prev, education: value === 'Any Education' ? '' : value }))
                        }>
                          <SelectTrigger>
                            <SelectValue placeholder="Any Education" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Any Education">Any Education</SelectItem>
                            <SelectItem value="Graduate">Graduate</SelectItem>
                            <SelectItem value="Post Graduate">Post Graduate</SelectItem>
                            <SelectItem value="Doctorate">Doctorate</SelectItem>
                            <SelectItem value="Diploma">Diploma</SelectItem>
                            <SelectItem value="CA">Chartered Accountant</SelectItem>
                            <SelectItem value="CS">Company Secretary</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-medium text-gray-700">Profession</Label>
                        <Select
                          value={filters.profession}
                          onValueChange={(value) =>
                            setFilters(prev => ({ ...prev, profession: value === 'Any Profession' ? '' : value }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Any Profession" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Any Profession">Any Profession</SelectItem>
                            <SelectItem value="Software Engineer">Software Engineer</SelectItem>
                            <SelectItem value="Doctor">Doctor</SelectItem>
                            <SelectItem value="CA">Chartered Accountant</SelectItem>
                            <SelectItem value="Business">Business</SelectItem>
                            <SelectItem value="Teacher">Teacher</SelectItem>
                            <SelectItem value="Government">Government Job</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Family Details */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900 flex items-center">
                      <Home className="h-4 w-4 mr-2 text-indigo-600" />
                      Family Details
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Family Type</Label>
                        <div className="space-y-2 mt-2">
                          {['Nuclear', 'Joint', 'Business'].map(type => (
                            <div key={type} className="flex items-center space-x-2">
                              <Checkbox
                                id={`family-${type}`}
                                checked={filters.familyType.includes(type)}
                                onCheckedChange={() => handleFamilyTypeChange(type)}
                              />
                              <Label htmlFor={`family-${type}`} className="text-sm">{type}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lifestyle & Habits */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Lifestyle & Habits</h4>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium text-gray-700">Diet</Label>
                        <div className="space-y-2 mt-2">
                          {['Vegetarian', 'Non-Vegetarian', 'Eggetarian', 'Vegan'].map(diet => (
                            <div key={diet} className="flex items-center space-x-2">
                              <Checkbox
                                id={`diet-${diet}`}
                                checked={filters.diet.includes(diet)}
                                onCheckedChange={() => handleDietChange(diet)}
                              />
                              <Label htmlFor={`diet-${diet}`} className="text-sm">{diet}</Label>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Additional Filters */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-gray-900">Additional Filters</h4>
                    <div className="space-y-3">
                      {[
                        { id: 'hasPhotos', label: 'Has Photos' },
                        { id: 'isVerified', label: 'Verified Profiles' },
                        { id: 'isPremium', label: 'Premium Members' },
                      ].map(filter => (
                        <div key={filter.id} className="flex items-center space-x-2">
                          <Checkbox
                            id={filter.id}
                            checked={filters[filter.id as keyof typeof filters] as boolean}
                            onCheckedChange={(checked) => 
                              setFilters(prev => ({ ...prev, [filter.id]: checked }))
                            }
                          />
                          <Label htmlFor={filter.id} className="text-sm">{filter.label}</Label>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Footer with Navigation Bar Color */}
                  <div className="mt-6 pt-6 border-t border-rose-200">
                    <div className="bg-gradient-to-r from-rose-500 to-pink-500 p-4 rounded-lg border border-rose-400">
                      <div className="flex items-center space-x-2 text-sm text-white">
                        <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                        <span className="font-medium">
                          {filteredProfiles.length} matches found
                          {totalPages > 1 && ` • Page ${currentPage}/${totalPages}`}
                        </span>
                      </div>
                      <p className="text-xs text-rose-100 mt-1">
                        {totalPages > 1 
                          ? `Showing ${currentProfiles.length} profiles per page` 
                          : 'Smart filters help you find better matches'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Profile Results */}
          <div className="lg:col-span-3">

            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                {loading ? 'Loading...' : (
                  <div>
                    <span>{filteredProfiles.length} Matches Found</span>
                    {totalPages > 1 && (
                      <span className="text-sm font-normal text-gray-600 ml-2">
                        (Page {currentPage} of {totalPages} - Showing {currentProfiles.length} profiles)
                      </span>
                    )}
                  </div>
                )}
              </h3>
              {!loading && (
                <div className="flex items-center space-x-2">
                  <Button variant="outline" className="bg-transparent">
                    <Briefcase className="h-4 w-4 mr-2" />
                    Sort by Profession
                  </Button>
                  <Button variant="outline" className="bg-transparent">
                    <Star className="h-4 w-4 mr-2" />
                    Sort by Match Score
                  </Button>
                </div>
              )}
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading real user profiles from database...</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {currentProfiles.map((profile) => (
                    <Card key={profile.userId} className="group hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 border-0 bg-white/80 backdrop-blur-sm">
                      <CardContent className="p-6">
                        {/* Profile Header */}
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className="relative">
                              <img
                                src={getDisplayPhoto(profile)}
                                alt={`${profile.firstName} ${profile.lastName}`}
                                className="w-16 h-16 rounded-full object-cover border-2 border-purple-200"
                                onError={(e) => {
                                  // Fallback placeholder
                                  (e.target as HTMLImageElement).src = 'https://pub-cdn.sider.ai/u/U0VEH8VKN6G/web-coder/68ec79a86b803a5b0f03c12e/resource/6abaa298-82f6-403d-843d-adf2388cd468.png';
                                }}
                              />
                            </div>
                            <div>
                              <div className="flex items-center space-x-2">
                                <h4 className="font-bold text-lg text-gray-900">{profile.firstName} {profile.lastName}</h4>
                                {profile.isVerified && (
                                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                                    Verified
                                  </Badge>
                                )}
                                {profile.isPremium === 1 || profile.isPremium === true ? (
                                  <Crown className="h-4 w-4 text-yellow-500" />
                                ) : null}
                              </div>
                              <div className="flex items-center space-x-2 text-sm text-gray-600">
                                <span>{profile.age} years</span>
                                <span>•</span>
                                <span>{profile.height}</span>
                              </div>
                              <div className="flex items-center space-x-2 text-xs text-gray-500 font-mono mt-1">
                                <span>ID: {profile.userId}</span>
                              </div>
                            </div>
                          </div>
                          <Badge className={`${
                            profile.matchScore >= 90 ? 'bg-green-100 text-green-700' :
                            profile.matchScore >= 80 ? 'bg-blue-100 text-blue-700' :
                            'bg-orange-100 text-orange-700'
                          }`}>
                            {profile.matchScore}%
                          </Badge>
                        </div>

                        {/* Professional Info */}
                        <div className="space-y-2 mb-4">
                          <div className="flex items-center text-sm text-gray-700">
                            <Briefcase className="h-4 w-4 mr-2 text-purple-600" />
                            {profile.profession || 'Not specified'} {'\u00A0\u00A0'}
                            {isShortlisted(profile.userId) && (
                                  <Badge className="bg-green-100 text-green-700 flex items-center px-2 py-1">
                                    <Heart className="h-3 w-3 mr-1 fill-current" />
                                    <span>Shortlisted</span>
                                  </Badge>
                                )}
                          </div>
                          <div className="flex items-center text-sm text-gray-700">
                            <GraduationCap className="h-4 w-4 mr-2 text-green-600" />
                            {profile.education || 'Not specified'}
                          </div>
                          <div className="flex items-center text-sm text-gray-700">
                            <MapPin className="h-4 w-4 mr-2 text-red-600" />
                            {profile.city}, {profile.state}
                          </div>
                        </div>

                        {/* Hobbies - Enhanced Display */}
                        <div className="text-sm mb-4">
                          <Label className="font-medium text-gray-700">Hobbies</Label>
                          <div className="flex flex-wrap gap-2 mt-1">
                            {profile.hobbies ? (
                              <>
                                {profile.hobbies.split(', ').slice(0, 3).map((hobby, index) => (
                                  <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">
                                    {hobby}
                                  </span>
                                ))}
                                {profile.hobbies.split(', ').length > 3 && (
                                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                    +{profile.hobbies.split(', ').length - 3} more
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="px-2 py-1 bg-gray-100 text-gray-500 text-xs rounded-full">
                                Hobbies not specified
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Action Buttons - DYNAMIC SHORTLIST */}
                        <div className="flex space-x-2 mt-4">
                          {process.env.NODE_ENV === 'development' &&
                            console.log(`🎯 RENDERING CARD: ${profile.firstName} ${profile.lastName} (ID: ${profile.userId}) - Shortlisted: ${isShortlisted(profile.userId)}`)}
                          
                          <Button 
                            size="sm" 
                            className={`flex-1 font-medium transition-all duration-300 ${
                              isShortlisted(profile.userId)
                                ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white'
                                : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white'
                            } ${isShortlisting.has(profile.userId) ? 'opacity-50 cursor-not-allowed' : ''}`}
                            onClick={() => handleShortlist(profile)}
                            disabled={isShortlisting.has(profile.userId)}
                          >
                            <Heart className={`h-3 w-3 mr-1 ${isShortlisted(profile.userId) ? 'fill-current' : ''}`} />
                            {isShortlisting.has(profile.userId) 
                              ? 'Processing...' 
                              : (isShortlisted(profile.userId) ? 'Shortlisted' : 'Short List')
                            }
                          </Button>
                          <Button
                            size="sm"
                            className="flex-1 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium"
                            onClick={() => {
                              // Record profile view before navigation
                              recordProfileView(profile.userId);
                              // Navigate to profile page
                              navigate(`/search-profile/${profile.userId}`);
                            }}
                          >
                            <User className="h-3 w-3 mr-1" />
                            View Profile
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-4 mt-8">
                    {/* Page Info - Mobile */}
                    <div className="sm:hidden text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </div>
                    
                    {/* Previous Button */}
                    <Button
                      onClick={prevPage}
                      disabled={currentPage === 1}
                      variant="outline"
                      className="bg-transparent border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      <span className="hidden sm:inline">Previous</span>
                    </Button>

                    {/* Page Numbers */}
                    <div className="flex space-x-1 sm:space-x-2">
                      {totalPages <= 7 ? (
                        Array.from({ length: totalPages }, (_, i) => i + 1).map((number) => (
                          <button
                            key={number}
                            onClick={() => paginate(number)}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${
                              currentPage === number
                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                                : 'bg-white text-gray-700 border border-gray-300 hover:border-purple-300 hover:text-purple-700'
                            }`}
                          >
                            {number}
                          </button>
                        ))
                      ) : (
                        <>
                          {/* First page button */}
                          <button
                            onClick={() => paginate(1)}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${
                              currentPage === 1
                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                                : 'bg-white text-gray-700 border border-gray-300 hover:border-purple-300 hover:text-purple-700'
                            }`}
                          >
                            1
                          </button>
                          
                          {/* Ellipsis for skipped pages */}
                          {currentPage > 4 && (
                            <span className="flex items-center justify-center text-gray-500 text-sm">
                              ...
                            </span>
                          )}

                          {/* Current page button (always show) */}
                          <button
                            className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg"
                          >
                            {currentPage}
                          </button>

                          {/* Ellipsis for skipped pages */}
                          {currentPage < totalPages - 3 && (
                            <span className="flex items-center justify-center text-gray-500 text-sm">
                              ...
                            </span>
                          )}

                          {/* Last page button */}
                          <button
                            onClick={() => paginate(totalPages)}
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all duration-200 ${
                              currentPage === totalPages
                                ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                                : 'bg-white text-gray-700 border border-gray-300 hover:border-purple-300 hover:text-purple-700'
                            }`}
                          >
                            {totalPages}
                          </button>
                        </>
                      )}
                    </div>

                    {/* Next Button */}
                    <Button
                      onClick={nextPage}
                      disabled={currentPage === totalPages}
                      variant="outline"
                      className="bg-transparent border-purple-300 text-purple-700 hover:bg-purple-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="hidden sm:inline">Next</span>
                      <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerSearch;